import React, { useState, useEffect, useRef } from 'react';
import '../styles/DatePicker.css';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function formatDate(date) {
  if (!date) return null;
  const month = MONTH_SHORT[date.getMonth()];
  const day   = date.getDate();
  const year  = date.getFullYear();
  return `${month} ${day}, ${year}`;
}

function toMidnight(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function parseYMD(ymd) {
  if (!ymd) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function toYMD(date) {
  if (!date) return '';
  const y   = date.getFullYear();
  const m   = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function DatePicker({ value, onChange, placeholder = 'Select date' }) {
  const today        = toMidnight(new Date());
  const selectedDate = value ? toMidnight(parseYMD(value)) : null;

  const [open,         setOpen]         = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const base = selectedDate || today;
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const selectDate = (date) => {
    onChange?.(date ? toYMD(date) : '');
    setOpen(false);
  };

  const prevMonth = () => setCurrentMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  const nextMonth = () => setCurrentMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1));

  const year        = currentMonth.getFullYear();
  const month       = currentMonth.getMonth();
  const firstDay    = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev  = new Date(year, month, 0).getDate();

  const days = [];

  for (let i = firstDay - 1; i >= 0; i--) {
    const d     = daysInPrev - i;
    const pm    = month === 0 ? 11 : month - 1;
    const py    = month === 0 ? year - 1 : year;
    days.push({ day: d, month: pm, year: py, otherMonth: true });
  }

  for (let d = 1; d <= daysInMonth; d++) {
    days.push({ day: d, month, year, otherMonth: false });
  }

  const total     = firstDay + daysInMonth;
  const remaining = total % 7 === 0 ? 0 : 7 - (total % 7);
  for (let d = 1; d <= remaining; d++) {
    const nm = month === 11 ? 0  : month + 1;
    const ny = month === 11 ? year + 1 : year;
    days.push({ day: d, month: nm, year: ny, otherMonth: true });
  }

  const handleDayClick = (entry) => {
    if (entry.otherMonth) {
      setCurrentMonth(new Date(entry.year, entry.month, 1));
      return;
    }
    selectDate(new Date(entry.year, entry.month, entry.day));
  };

  const getDayClasses = (entry) => {
    const classes = ['calendar-day'];
    if (entry.otherMonth) { classes.push('other-month'); return classes.join(' '); }
    const d = toMidnight(new Date(entry.year, entry.month, entry.day));
    if (d.getTime() === today.getTime()) classes.push('today');
    if (selectedDate && d.getTime() === selectedDate.getTime()) classes.push('selected');
    return classes.join(' ');
  };

  const displayText = selectedDate ? formatDate(selectedDate) : null;

  return (
    <div className="custom-date-picker" ref={containerRef}>
      <div
        className={`date-input-wrapper${open ? ' active' : ''}`}
        onClick={() => setOpen(v => !v)}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-label={displayText || placeholder}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(v => !v); } }}
      >
        <span className={`date-display${!displayText ? ' placeholder' : ''}`}>
          {displayText || placeholder}
        </span>
        <svg className="calendar-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
          <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z"/>
        </svg>
      </div>

      <div className={`calendar-dropdown${open ? ' active' : ''}`} role="dialog" aria-label="Calendar">
        {/* Header */}
        <div className="calendar-header">
          <button className="calendar-nav-btn" aria-label="Previous month" onClick={e => { e.stopPropagation(); prevMonth(); }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"/>
            </svg>
          </button>
          <div className="calendar-month-year" aria-live="polite">{MONTH_NAMES[month]} {year}</div>
          <button className="calendar-nav-btn" aria-label="Next month" onClick={e => { e.stopPropagation(); nextMonth(); }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
            </svg>
          </button>
        </div>

        {/* Weekday labels */}
        <div className="calendar-weekdays">
          {WEEKDAYS.map(d => <div key={d} className="calendar-weekday">{d}</div>)}
        </div>

        {/* Days grid */}
        <div className="calendar-days" role="grid">
          {days.map((entry, i) => (
            <div
              key={i}
              className={getDayClasses(entry)}
              role="gridcell"
              onClick={e => { e.stopPropagation(); handleDayClick(entry); }}
              aria-label={`${MONTH_NAMES[entry.month]} ${entry.day}, ${entry.year}`}
            >
              {entry.day}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="calendar-footer">
          <button className="calendar-btn calendar-btn-clear" onClick={e => { e.stopPropagation(); selectDate(null); }}>Clear</button>
          <button className="calendar-btn calendar-btn-today" onClick={e => { e.stopPropagation(); selectDate(new Date(today)); }}>Today</button>
        </div>
      </div>
    </div>
  );
}